'use client';

import * as React from 'react';

import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';

import { cn } from '@/lib/utils';

import { useTouchPrimary } from '@/hooks/use-has-primary-touch';

const ScrollAreaContext = React.createContext<boolean>(false);
type Mask = {
  top: boolean;
  bottom: boolean;
  left: boolean;
  right: boolean;
};
type ViewportProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'children' | 'className' | 'style'
> &
  Partial<
    Record<`data-${string}`, string | number | boolean | null | undefined>
  >;

const ScrollArea = React.forwardRef<
  React.ComponentRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
    viewportClassName?: string;
    viewportStyle?: React.CSSProperties;
    viewportProps?: ViewportProps;
    /**
     * `maskHeight` is the height of the mask in pixels.
     * pass `0` to disable the mask
     * @default 30
     */
    maskHeight?: number;
    maskClassName?: string;
    /**
     * Optional ref to expose the viewport element for scroll restoration
     */
    viewportRef?: React.ForwardedRef<HTMLDivElement>;
    /**
     * Marks this scroll viewport as the primary page-level scroll container
     * for global affordances like the scroll-to-top button.
     */
    scrollTopContainer?: boolean;
    showHorizontalScrollbar?: boolean;
    /**
     * Stop wheel/touch scroll from chaining to ancestors, but only while the
     * viewport actually overflows vertically. Chromium applies
     * `overscroll-behavior` to non-overflowing scroll containers too, so a
     * static `overscroll-contain` class swallows page scroll over short lists.
     */
    containOverscrollWhenScrollable?: boolean;
  }
>(
  (
    {
      className,
      children,
      scrollHideDelay = 0,
      viewportClassName,
      viewportStyle,
      viewportProps,
      maskClassName,
      maskHeight = 30,
      viewportRef: externalViewportRef,
      scrollTopContainer = false,
      showHorizontalScrollbar = true,
      containOverscrollWhenScrollable = false,
      ...props
    },
    ref
  ) => {
    const [showMask, setShowMask] = React.useState<Mask>({
      top: false,
      bottom: false,
      left: false,
      right: false,
    });
    const [scrollableY, setScrollableY] = React.useState(false);
    const internalViewportRef = React.useRef<HTMLDivElement>(null);
    const scrollCheckFrameRef = React.useRef<number | null>(null);
    const isTouch = useTouchPrimary();

    // Expose the internal viewport via the external ref. Re-run only when the
    // pointer mode swaps the viewport element, so a callback ref is told about
    // the NEW node (and not re-invoked on every render).
    React.useImperativeHandle(
      externalViewportRef,
      () => internalViewportRef.current as HTMLDivElement,
      // eslint-disable-next-line react-hooks/exhaustive-deps -- isTouch swaps the viewport element the handle points at
      [isTouch]
    );

    const checkScrollability = React.useCallback(() => {
      const element = internalViewportRef.current;
      if (!element) return;

      const {
        scrollTop,
        scrollLeft,
        scrollWidth,
        clientWidth,
        scrollHeight,
        clientHeight,
      } = element;

      const computed = window.getComputedStyle(element);
      const overflowX = computed.overflowX;
      const horizontalHidden = overflowX === 'hidden' || overflowX === 'clip';

      setScrollableY(scrollHeight > clientHeight + 1);

      setShowMask((prev) => {
        const next = {
          top: scrollTop > 0,
          bottom: scrollTop + clientHeight < scrollHeight - 1,
          left: horizontalHidden ? false : scrollLeft > 0,
          right: horizontalHidden
            ? false
            : scrollLeft + clientWidth < scrollWidth - 1,
        };

        if (
          prev.top === next.top &&
          prev.bottom === next.bottom &&
          prev.left === next.left &&
          prev.right === next.right
        ) {
          return prev;
        }

        return next;
      });
    }, []);

    const scheduleScrollabilityCheck = React.useCallback(() => {
      if (typeof window === 'undefined') return;
      if (scrollCheckFrameRef.current !== null) return;

      scrollCheckFrameRef.current = window.requestAnimationFrame(() => {
        scrollCheckFrameRef.current = null;
        checkScrollability();
      });
    }, [checkScrollability]);

    React.useEffect(() => {
      if (typeof window === 'undefined') return;
      if (maskHeight <= 0 && !containOverscrollWhenScrollable) return;

      const element = internalViewportRef.current;
      if (!element) return;

      const controller = new AbortController();
      const { signal } = controller;

      // Without ResizeObserver (older engines, jsdom) the scroll and window
      // resize listeners plus the initial check still keep state current.
      const resizeObserver =
        typeof ResizeObserver === 'undefined'
          ? null
          : new ResizeObserver(scheduleScrollabilityCheck);
      resizeObserver?.observe(element);
      // Content growth past a capped max-height doesn't resize the viewport,
      // so watch the content wrapper too.
      if (element.firstElementChild) {
        resizeObserver?.observe(element.firstElementChild);
      }

      element.addEventListener('scroll', scheduleScrollabilityCheck, {
        signal,
        passive: true,
      });
      window.addEventListener('resize', scheduleScrollabilityCheck, { signal });

      // Run an initial check whenever dependencies change (including pointer mode)
      checkScrollability();

      return () => {
        controller.abort();
        resizeObserver?.disconnect();
        if (scrollCheckFrameRef.current !== null) {
          window.cancelAnimationFrame(scrollCheckFrameRef.current);
          scrollCheckFrameRef.current = null;
        }
      };
    }, [
      checkScrollability,
      containOverscrollWhenScrollable,
      isTouch,
      maskHeight,
      scheduleScrollabilityCheck,
    ]);

    const overscrollClassName =
      containOverscrollWhenScrollable && scrollableY
        ? 'overscroll-contain'
        : undefined;

    return (
      <ScrollAreaContext.Provider value={isTouch}>
        {isTouch ? (
          <div
            ref={ref}
            role="group"
            data-slot="scroll-area"
            aria-roledescription="scroll area"
            className={cn('relative overflow-hidden', className)}
            {...props}
          >
            <div
              {...viewportProps}
              ref={internalViewportRef}
              data-slot="scroll-area-viewport"
              data-scroll-top-container={
                scrollTopContainer ? 'true' : undefined
              }
              className={cn(
                'size-full overflow-auto rounded-[inherit]',
                overscrollClassName,
                viewportClassName
              )}
              style={viewportStyle}
              tabIndex={0}
            >
              {children}
            </div>

            {maskHeight > 0 && (
              <ScrollMask
                showMask={showMask}
                className={maskClassName}
                maskHeight={maskHeight}
              />
            )}
          </div>
        ) : (
          <ScrollAreaPrimitive.Root
            ref={ref}
            data-slot="scroll-area"
            scrollHideDelay={scrollHideDelay}
            className={cn('relative overflow-hidden', className)}
            {...props}
          >
            <ScrollAreaPrimitive.Viewport
              {...viewportProps}
              ref={internalViewportRef}
              data-slot="scroll-area-viewport"
              data-scroll-top-container={
                scrollTopContainer ? 'true' : undefined
              }
              className={cn(
                'size-full rounded-[inherit]',
                overscrollClassName,
                viewportClassName
              )}
              style={viewportStyle}
            >
              {children}
            </ScrollAreaPrimitive.Viewport>

            {maskHeight > 0 && (
              <ScrollMask
                showMask={showMask}
                className={maskClassName}
                maskHeight={maskHeight}
              />
            )}
            <ScrollBar orientation="vertical" />
            {showHorizontalScrollbar ? (
              <ScrollBar orientation="horizontal" />
            ) : null}
            <ScrollAreaPrimitive.Corner />
          </ScrollAreaPrimitive.Root>
        )}
      </ScrollAreaContext.Provider>
    );
  }
);

ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ComponentRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = 'vertical', ...props }, ref) => {
  const isTouch = React.useContext(ScrollAreaContext);

  if (isTouch) return null;

  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      ref={ref}
      orientation={orientation}
      data-slot="scroll-area-scrollbar"
      className={cn(
        'hover:bg-muted dark:hover:bg-muted/50 data-[state=visible]:fade-in-0 data-[state=hidden]:fade-out-0 data-[state=visible]:animate-in data-[state=hidden]:animate-out z-50 flex touch-none p-px transition-[width,height,background-color] duration-150 select-none',
        orientation === 'vertical' && 'h-full w-1 border-l-0 hover:w-2',
        orientation === 'horizontal' &&
          'h-1 flex-col border-t-0 px-1 pr-1.25 hover:h-2',
        className
      )}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb
        data-slot="scroll-area-thumb"
        className={cn(
          'bg-border/60 relative flex-1 origin-center rounded-full transition-[transform,background-color]',
          orientation === 'vertical' && 'my-1 active:scale-y-95',
          orientation === 'horizontal' && 'active:scale-x-98'
        )}
      />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  );
});

ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

const ScrollMask = ({
  showMask,
  maskHeight,
  className,
  ...props
}: React.ComponentProps<'div'> & {
  showMask: Mask;
  maskHeight: number;
}) => {
  return (
    <>
      <div
        {...props}
        aria-hidden="true"
        style={
          {
            '--top-fade-height': showMask.top ? `${maskHeight}px` : '0px',
            '--bottom-fade-height': showMask.bottom ? `${maskHeight}px` : '0px',
          } as React.CSSProperties
        }
        className={cn(
          'pointer-events-none absolute inset-0 z-10',
          "before:absolute before:inset-x-0 before:top-0 before:transition-[height,opacity] before:duration-300 before:content-['']",
          "after:absolute after:inset-x-0 after:bottom-0 after:transition-[height,opacity] after:duration-300 after:content-['']",
          'before:h-(--top-fade-height) after:h-(--bottom-fade-height)',
          showMask.top ? 'before:opacity-100' : 'before:opacity-0',
          showMask.bottom ? 'after:opacity-100' : 'after:opacity-0',
          'before:from-background before:bg-gradient-to-b before:to-transparent',
          'after:from-background after:bg-gradient-to-t after:to-transparent',
          className
        )}
      />
      <div
        {...props}
        aria-hidden="true"
        style={
          {
            '--left-fade-width': showMask.left ? `${maskHeight}px` : '0px',
            '--right-fade-width': showMask.right ? `${maskHeight}px` : '0px',
          } as React.CSSProperties
        }
        className={cn(
          'pointer-events-none absolute inset-0 z-10',
          "before:absolute before:inset-y-0 before:left-0 before:transition-[width,opacity] before:duration-300 before:content-['']",
          "after:absolute after:inset-y-0 after:right-0 after:transition-[width,opacity] after:duration-300 after:content-['']",
          'before:w-(--left-fade-width) after:w-(--right-fade-width)',
          showMask.left ? 'before:opacity-100' : 'before:opacity-0',
          showMask.right ? 'after:opacity-100' : 'after:opacity-0',
          'before:from-background before:bg-gradient-to-r before:to-transparent',
          'after:from-background after:bg-gradient-to-l after:to-transparent',
          className
        )}
      />
    </>
  );
};

export { ScrollArea, ScrollBar };
